/**
 * A notice in a panel: a raised line ringed in its tone — marigold to look
 * again, coral when something stands in the way, gold for what needs you,
 * jade for something done right —
 * its mark in the ring's colour, a headline and a quieter detail under it.
 * The configurator says with it that rows were trimmed or a cabin cannot be
 * saved; the route builder, why a flight cannot go — no aircraft reaches the
 * city, no flight slot is free; the team's activity, that a city on the globe
 * waits for the player's click. A notice that needs you is tinted with its
 * gold as well as ringed: it is a call to act where the act itself is
 * elsewhere (the globe), and it is not a button. `onNight` sets one on a
 * night board — the board's dark well for its plate and the board's lavender
 * for its words — as the HQ vote's result says a vote was drawn at random.
 * `action` sets a control at its end — the close of the admin's quiz
 * preview, whose notice says the answers stay on the screen.
 *
 * It announces nothing itself: the caller keeps a live region on the page
 * and puts the notice in it, since a region that arrives with its words is
 * not announced.
 *
 *   <Notice tone="danger" glyph={GLYPH.warning} title="Outside your network" detail="A new route must…" />
 */

import type { ReactNode } from 'react';

import type { Glyph } from './glyphs';
import { Icon } from './icon';
import styles from './notice.module.css';

const TONE = {
  warning: styles.warning,
  danger: styles.danger,
  needsYou: styles.needsYou,
  success: styles.success,
} as const;

interface NoticeProps {
  /** Marigold to look again; coral when something stands in the way; gold for what needs you; jade for done right. */
  tone: keyof typeof TONE;
  glyph: Glyph;
  title: ReactNode;
  detail?: ReactNode;
  /** On a night board: the board's well and lavender in place of the raised plate and the slate's inks. */
  onNight?: boolean;
  /** A control at its end: a close. */
  action?: ReactNode;
}

export function Notice({ tone, glyph, title, detail, onNight = false, action }: NoticeProps) {
  return (
    <div className={[styles.notice, TONE[tone], onNight ? styles.onNight : ''].filter(Boolean).join(' ')}>
      <span className={styles.mark}>
        <Icon glyph={glyph} size="md" />
      </span>
      <p className={styles.text}>
        <span className={styles.title}>{title}</span>
        {detail != null && <span className={styles.detail}>{detail}</span>}
      </p>
      {action != null && <span className={styles.action}>{action}</span>}
    </div>
  );
}
